/**
 * Centralized service for managing loading indicators with custom gift card animation
 * Uses Observable-based state management for custom component (no Ionic LoadingController)
 */

import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { LoaderMessage } from '../model/loader.types';

@Injectable({
  providedIn: 'root'
})
export class LoaderService {

  // State management
  private isLoadingSubject$ = new BehaviorSubject<boolean>(false);
  private messageSubject$ = new BehaviorSubject<string>(LoaderMessage.LOADING);

  // Public observables
  public isLoading$ = this.isLoadingSubject$.asObservable();
  public currentMessage$ = this.messageSubject$.asObservable();

  // Reference counting for nested calls (prevents early hide)
  private ref = 0;

  constructor() {}

  /**
   * Show loader with custom or predefined message
   * Uses reference counting to handle nested calls safely
   */
  async show(message: string = LoaderMessage.LOADING): Promise<void> {
    this.messageSubject$.next(message);

    // Only show loader when going from 0 → 1
    if (++this.ref === 1) {
      this.isLoadingSubject$.next(true);
      if (typeof document !== 'undefined') {
        document.body.classList.add('no-scroll');
      }
    }
  }

  /**
   * Hide the current loader
   * Only hides when all show() calls have been matched with hide()
   */
  async hide(): Promise<void> {
    // Only hide when going back to 0
    if (this.ref > 0 && --this.ref === 0) {
      this.isLoadingSubject$.next(false);
      if (typeof document !== 'undefined') {
        document.body.classList.remove('no-scroll');
      }
    }
  }

  /**
   * Execute an async operation with automatic loader management
   * Shows loader before operation and hides it after completion
   */
  async withLoader<T>(
    operation: () => Promise<T>,
    message: string = LoaderMessage.LOADING
  ): Promise<T> {
    await this.show(message);
    try {
      return await operation();
    } finally {
      await this.hide();
    }
  }

  /**
   * Check if a loader is currently showing
   */
  isLoading(): boolean {
    return this.isLoadingSubject$.value;
  }

  /**
   * Get current message (for debugging)
   */
  get currentMessage(): string {
    return this.messageSubject$.value;
  }
}
